import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Layouts
import MainLayout from './components/layout/MainLayout';
import PlaybookLayout from './components/layout/PlaybookLayout';

// Components
import Logo from './components/common/Logo';
import LoadingSpinner from './components/common/LoadingSpinner';
import ScrollToTop from './components/common/ScrollToTop';

// Pages
import ContentLibrary from './pages/ContentLibrary';
import Dashboard from './pages/Dashboard';
import EducationalResources from './pages/Courses';
import Discounts from './pages/Discounts';
import DiscountDetail from './pages/discounts/DiscountDetail';
import Collection from './pages/Collection';
import Account from './pages/Account';
import Toolkit from './pages/toolkit/Toolkit';
import NotFound from './pages/NotFound';
import PlaybooksIndex from './pages/playbooks';
import PlaybookSection from './pages/playbooks/[playbookId]/[sectionId]';
import ChatPage from './pages/ChatPage';
import Admin from './pages/Admin';

// Auth pages
import SignIn from './pages/auth/SignIn';  
import SignUp from './pages/auth/Signup';
import Payment from './pages/auth/Payment';
import PaymentSuccess from './pages/auth/PaymentSuccess';
import AuthRedirect from './pages/auth/AuthRedirect';
import EmailConfirmation from './pages/auth/EmailConfirmation';

// Import the Terms and Privacy pages
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

// Onboarding
import OnboardingFlow from './pages/onboarding/OnboardingFlow';

// Styles
import './App.css';

// Authentication Context
import { AuthProvider } from './context/AuthContext';
import { useAuthState } from './hooks/useAuthState';

function ProtectedRoute({ children, requireOnboarding, requireSubscription, allowUnauthenticated }) {
  const location = useLocation();
  const { 
    isLoading,
    isAuthenticated,
    isUnauthenticated,
    hasCompletedOnboarding,
    hasActiveSubscription
  } = useAuthState();
  
  // Show loading spinner only during initial auth load (user === null)
  // This prevents spinner from showing during temporary auth events
  if (isLoading) {
    return (
      <LoadingSpinner 
        fullScreen 
        message="Loading..." 
        size="lg"
      />
    );
  }
  
  // Allow unauthenticated access if specified
  if (allowUnauthenticated && !isAuthenticated) {
    return children;
  }
  
  // Handle not authenticated
  if (isUnauthenticated) {
    return <Navigate to="/signin" state={{ from: location }} />;
  }
  
  // Check other requirements
  if (requireOnboarding && !hasCompletedOnboarding) {
    return <Navigate to="/onboarding" />;
  }
  
  if (requireSubscription && !hasActiveSubscription) {
    return <Navigate to="/payment" />;
  }
  
  return children;
}

function PublicPage({ children }) {
  return (
    <motion.div
      className="flex min-h-screen flex-col bg-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-0 sm:py-12">
        <div className="mb-8 text-center">
          <Logo size="large" centered={true} />
        </div>
        {children}
      </div>
    </motion.div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true }}>
        <ScrollToTop />
        <AnimatePresence mode="wait">
          <Routes>
            {/* Auth redirect for OAuth */}
            <Route path="/auth-redirect" element={<AuthRedirect />} />
            {/* Auth routes */}
            <Route path="/signin" element={<PublicPage><SignIn /></PublicPage>} />
            <Route path="/signup" element={<PublicPage><SignUp /></PublicPage>} />
            {/* Email confirmation route */}
            <Route path="/email-confirmation" element={
              <ProtectedRoute>
                <PublicPage><EmailConfirmation /></PublicPage>
              </ProtectedRoute>
            } />
            {/* Onboarding route */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <PublicPage><OnboardingFlow /></PublicPage>
              </ProtectedRoute>
            } />
            {/* Payment route */}
            <Route path="/payment" element={
              <ProtectedRoute requireOnboarding>
                <PublicPage><Payment /></PublicPage>
              </ProtectedRoute>
            } />
            {/* Payment Success route - bypasses ProtectedRoute to avoid redirect conflicts */}
            <Route path="/payment-success" element={<PublicPage><PaymentSuccess /></PublicPage>} />
        
            {/* Playbook routes - require user, onboarding, and subscription */}
            <Route element={
              <ProtectedRoute requireOnboarding requireSubscription>
                <PlaybookLayout />
              </ProtectedRoute>
            }>
              <Route path="/playbooks" element={<PlaybooksIndex />} />
              <Route path="/playbooks/:playbookId/:sectionId" element={<PlaybookSection />} />
            </Route>
           
            
            {/* Main app routes - require user, onboarding, and subscription */}
            <Route element={
              <ProtectedRoute requireOnboarding requireSubscription>
                <MainLayout />
              </ProtectedRoute>
            }>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/content" element={<ContentLibrary />} />
              <Route path="/courses" element={<EducationalResources />} />
              <Route path="/marketplace" element={<Discounts />} />
              <Route path="/marketplace/:id" element={<DiscountDetail />} />
              <Route path="/collection" element={<Collection />} />
              <Route path="/toolkit" element={<Toolkit />} />
              <Route path="/resources" element={<ContentLibrary />} />
              <Route path="/favorites" element={<Discounts />} />
              <Route path="/account" element={<Account />} />
              <Route path="/admin" element={<Admin />} />
            </Route>
            {/* Terms and Privacy */}
            <Route path="/terms" element={<PublicPage><Terms /></PublicPage>} />
            <Route path="/privacy" element={<PublicPage><Privacy /></PublicPage>} />
            {/* Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AnimatePresence>
      </Router>
    </AuthProvider>
  );
}